import apiClient from './api';
import { Document, BulkProcessingStatus, DocumentClassification, LinkedDocument } from '../types';

/**
 * Service module for document API operations.
 */

/** API response wrapper for documents list */
interface DocumentsApiResponse {
  documents: Array<{
    document_id: string;
    user_id: string;
    document_type: string;
    s3_key: string;
    verification_status: string;
    expiration_date?: string | null;
    uploaded_at: string;
    crew_id?: string;
    metadata?: Record<string, unknown>;
  }>;
}

/** Transform API document to frontend Document type */
function transformDocument(apiDoc: DocumentsApiResponse['documents'][0]): Document {
  return {
    documentId: apiDoc.document_id,
    userId: apiDoc.user_id,
    documentType: apiDoc.document_type,
    s3Key: apiDoc.s3_key,
    verificationStatus: apiDoc.verification_status as Document['verificationStatus'],
    expirationDate: apiDoc.expiration_date || undefined,
    uploadedAt: apiDoc.uploaded_at,
    crewId: apiDoc.crew_id,
    metadata: apiDoc.metadata || {},
  };
}

export const documentService = {
  /** Upload a document (individual file or ZIP). */
  uploadDocument: async (
    file: File,
    metadata?: { 
      documentType?: string; 
      crewId?: string;
      holderName?: string;
      issuer?: string;
      issueDate?: string;
      expiryDate?: string;
      certificateNumber?: string;
    }
  ): Promise<Document> => {
    // Convert file to base64
    const fileContent = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        // Remove data URL prefix (e.g., "data:application/pdf;base64,")
        const base64 = result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

    const payload: Record<string, unknown> = {
      filename: file.name,
      file_size: file.size,
      content_type: file.type || 'application/octet-stream',
      file_content: fileContent,
      document_type: metadata?.documentType || 'unknown',
      crew_id: metadata?.crewId,
    };

    // Add extracted metadata if available
    if (metadata?.holderName) payload.holder_name = metadata.holderName;
    if (metadata?.issuer) payload.issuer = metadata.issuer;
    if (metadata?.issueDate) payload.issue_date = metadata.issueDate;
    if (metadata?.expiryDate) payload.expiry_date = metadata.expiryDate;
    if (metadata?.certificateNumber) payload.certificate_number = metadata.certificateNumber;

    const response = await apiClient.post<{ document: DocumentsApiResponse['documents'][0] }>('/documents', payload);
    const doc = response.data.document || response.data;
    return transformDocument(doc);
  },

  /** List all documents for the authenticated user. */
  getDocuments: async (): Promise<Document[]> => {
    const response = await apiClient.get<DocumentsApiResponse>('/documents');
    const documents = response.data.documents || [];
    return documents.map(transformDocument);
  },

  /** Delete a document by ID. */
  deleteDocument: async (documentId: string): Promise<void> => {
    await apiClient.delete(`/documents/${documentId}`);
  },

  /** Get a signed download URL for a document. */
  getDownloadUrl: async (documentId: string): Promise<string> => {
    const response = await apiClient.get<{ url: string }>(`/documents/${documentId}/download`);
    return response.data.url;
  },

  /** Get bulk processing status for a ZIP upload job. */
  getBulkStatus: async (jobId: string): Promise<BulkProcessingStatus> => {
    const response = await apiClient.get<BulkProcessingStatus>(`/documents/bulk/${jobId}`);
    return response.data;
  },

  /**
   * Classify a document without saving it.
   * Requirements: 14.1, 14.2, 14.3
   */
  classifyDocument: async (file: File): Promise<DocumentClassification> => {
    // Convert file to base64
    const fileContent = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        const base64 = result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

    const response = await apiClient.post<{
      classification: {
        suggestedType: string;
        confidence: number;
        isCrewDocument: boolean;
        extractedData: Record<string, unknown>;
      };
    }>('/documents/classify', {
      filename: file.name,
      file_content: fileContent,
    });

    const c = response.data.classification;
    return {
      suggestedType: c.suggestedType,
      confidence: c.confidence,
      isCrewDocument: c.isCrewDocument,
      extractedData: {
        holderName: c.extractedData.holder_name as string | undefined,
        vesselName: c.extractedData.vessel_name as string | undefined,
        expiryDate: c.extractedData.expiry_date as string | undefined,
        issueDate: c.extractedData.issue_date as string | undefined,
        issuer: c.extractedData.issuer as string | undefined,
        certificateNumber: c.extractedData.certificate_number as string | undefined,
        callSign: c.extractedData.call_sign as string | undefined,
        mmsi: c.extractedData.mmsi as string | undefined,
      },
    };
  },

  /**
   * Get all documents linked to a crew member.
   * Requirements: 13.3
   */
  getCrewDocuments: async (crewId: string): Promise<LinkedDocument[]> => {
    const response = await apiClient.get<{
      crew_id: string;
      crew_name: string;
      documents: Array<{
        document_id: string;
        document_type: string;
        verification_status: string;
        expiration_date?: string;
        uploaded_at: string;
        metadata?: Record<string, unknown>;
      }>;
    }>(`/crew/${encodeURIComponent(crewId)}/documents`);

    return response.data.documents.map((doc) => ({
      documentId: doc.document_id,
      documentType: doc.document_type,
      crewId: response.data.crew_id,
      crewName: response.data.crew_name,
      verificationStatus: doc.verification_status as 'pending' | 'verified' | 'failed' | 'expired',
      expiryDate: doc.expiration_date,
    }));
  },
};
